import React, {useState, useEffect} from 'react';
import {asignaturasAPI} from '../../services/api';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';
import Modal from '../Common/Modal';
import SubjectForm from '../Forms/SubjectForm';
import SearchFilter from '../Common/SearchFilter';
import DataTable from '../Common/DataTable';
import {PencilIcon, TrashIcon, PlusIcon} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {useAuth} from '../../context/AuthContext';
import {filterBySearchTerm} from '../../services/utils';

const Subjects = () => {
    const {isReadOnly} = useAuth();
    const [subjects, setSubjects] = useState([]);
    const [filteredSubjects, setFilteredSubjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchSubjects();
    }, []);

    const fetchSubjects = async () => {
        try {
            setLoading(true);
            const response = await asignaturasAPI.getAll();
            setSubjects(response.data);
            setFilteredSubjects(filterSubjectsByTerm(response.data, searchTerm));
            setError(null);
        } catch (err) {
            setError('Error al cargar las asignaturas');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const filterSubjectsByTerm = (subjectsData, term) => {
        return filterBySearchTerm(subjectsData, term, (subject, lowerTerm) => {
                const levelText = subject.Nivel === null || subject.Nivel === undefined
                    ? '-'
                    : String(subject.Nivel);

                return subject.Nombre.toLowerCase().includes(lowerTerm) || levelText.includes(lowerTerm);
            });
    };

    const handleSearch = (searchTerm) => {
        setSearchTerm(searchTerm);
        setFilteredSubjects(filterSubjectsByTerm(subjects, searchTerm));
    };

    const handleAdd = () => {
        setSelectedSubject(null);
        setIsModalOpen(true);
    };

    const handleEdit = (subject) => {
        setSelectedSubject(subject);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('¿Está seguro de eliminar esta asignatura?')) {
            try {
                await asignaturasAPI.delete(id);
                toast.success('Asignatura eliminada correctamente');
                fetchSubjects();
            } catch (err) {
                toast.error('Error al eliminar la asignatura');
                console.error(err);
            }
        }
    };

    const handleSubmit = async (formData) => {
        try {
            if (selectedSubject) {
                await asignaturasAPI.update(selectedSubject.IDAsignatura, formData);
                toast.success('Asignatura actualizada correctamente');
            } else {
                await asignaturasAPI.create(formData);
                toast.success('Asignatura creada correctamente');
            }
            setIsModalOpen(false);
            fetchSubjects();
        } catch (err) {
            toast.error('Error al guardar la asignatura');
            console.error(err);
        }
    };

    const columns = [
        {
            key: 'Nombre',
            label: 'Nombre',
            sortable: true
        }, {
            key: 'Nivel',
            label: 'Nivel',
            sortable: true,
            render: (value) => (
                <span
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {value === null || value === undefined ? '-' : `Nivel ${value}`}
                </span>
            )
        }
    ];

    // Agregar columna de acciones solo si no es readonly
    if (!isReadOnly()) {
        columns.push({
            key: 'actions',
            label: 'Acciones',
            render: (_, subject) => (
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleEdit(subject)}
                        className="text-primary-600 hover:text-primary-900"
                        title="Editar">
                        <PencilIcon className="h-5 w-5"/>
                    </button>
                    <button
                        onClick={() => handleDelete(subject.IDAsignatura)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar">
                        <TrashIcon className="h-5 w-5"/>
                    </button>
                </div>
            )
        });
    }

    if (loading) 
        return <LoadingSpinner text="Cargando asignaturas..."/>;
    if (error) 
        return <ErrorMessage message={error} onRetry={fetchSubjects}/>;
    
    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">Asignaturas</h1>
                {
                    !isReadOnly() && (
                        <button onClick={handleAdd} className="btn-primary">
                            <PlusIcon className="h-5 w-5 mr-2"/>
                            Nueva Asignatura
                        </button>
                    )
                }
            </div>

            <SearchFilter
                onSearch={handleSearch}
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Buscar por nombre o nivel..."/>

            <div className="card">
                <DataTable
                    columns={columns}
                    data={filteredSubjects}
                    emptyMessage="No hay asignaturas registradas"/>
            </div>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={selectedSubject
                    ? 'Editar Asignatura'
                    : 'Nueva Asignatura'}
                size="sm">
                <SubjectForm
                    subject={selectedSubject}
                    onSubmit={handleSubmit}
                    onCancel={() => setIsModalOpen(false)}/>
            </Modal>
        </div>
    );
};

export default Subjects;
